"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useQuery = void 0;
const utils_1 = require("@obvio/utils");
const react_1 = require("react");
const fetching_1 = require("./fetching");
const useCallbackOrQueryMutation_1 = require("./useCallbackOrQueryMutation");
const useClient_1 = require("./useClient");
const generated_client_1 = require("../constants/generated.client");
const lib_1 = require("../lib");
function useQuery(query, ...args) {
    const client = (0, useClient_1.useClient)();
    const [allOptions = {}] = args;
    const _a = allOptions, { withoutArgs = 'WAIT' } = _a, options = __rest(_a, ["withoutArgs"]);
    const argsReady = options.args !== null;
    const mounted = (0, react_1.useRef)(false);
    const fromCache = client === null || client === void 0 ? void 0 : client.queryCache(query, options);
    const [{ data, loading, error, refetchTrigger }, dispatch] = (0, lib_1.useReducer)(useCallbackOrQueryMutation_1.dataReducer, {
        data: fromCache,
        loading: !fromCache,
    });
    const serialized = (0, fetching_1.serialize)('query', query, options);
    (0, react_1.useEffect)(() => {
        if (fromCache && data && fromCache !== data) {
            dispatch('SET_DATA', fromCache);
        }
    }, [fromCache, data]);
    const { id, slug } = options.args || {};
    const prevId = (0, lib_1.usePreviousValue)(id);
    const prevSlug = (0, lib_1.usePreviousValue)(slug);
    const prevArgsReady = (0, lib_1.usePreviousValue)(argsReady);
    (0, react_1.useEffect)(() => {
        if (prevArgsReady && (!argsReady || prevId !== id || prevSlug !== slug)) {
            if (argsReady || withoutArgs === 'WAIT') {
                dispatch('REFETCH');
            }
            dispatch('RESET');
        }
    }, [prevId, id, prevSlug, slug, prevArgsReady, argsReady, withoutArgs]);
    const execute = (0, react_1.useCallback)((initial) => __awaiter(this, void 0, void 0, function* () {
        try {
            const res = yield (client === null || client === void 0 ? void 0 : client.query(query, Object.assign(Object.assign({}, options), (initial ? {} : { fetchPolicy: 'networkOnly' }))));
            if ('onSuccess' in options) {
                void (options === null || options === void 0 ? void 0 : options.onSuccess(res));
            }
            dispatch('SET_DATA', res);
        }
        catch (error_) {
            dispatch('SET_ERROR', (0, utils_1.getErrorMessage)(error_, 'Something went wrong'));
        }
    }), [client, options, query]);
    (0, react_1.useEffect)(() => client === null || client === void 0 ? void 0 : client.subscribeRefetchQueries(query, () => dispatch('REFETCH')), [query, client, execute]);
    (0, react_1.useEffect)(() => {
        if (serialized === 'not_ready') {
            return;
        }
        const initial = !mounted.current;
        if (initial) {
            mounted.current = true;
            if (data) {
                if ('onSuccess' in options) {
                    void (options === null || options === void 0 ? void 0 : options.onSuccess(data));
                }
                return;
            }
        }
        dispatch('LOADING');
        void execute(initial);
    }, [serialized, refetchTrigger]);
    const fetchMore = (0, react_1.useCallback)((queryArgs) => __awaiter(this, void 0, void 0, function* () {
        var _b, _c;
        if (loading || !argsReady) {
            throw new Error(`You can’t run fetchMore while the query ${query} is still waiting`);
        }
        if (error) {
            return false;
        }
        dispatch('LOADING');
        try {
            const cursorArg = generated_client_1.CURSOR_ARGS[query];
            const cursor = cursorArg &&
                (Array.isArray(data)
                    ? (_b = (0, utils_1.last)(data)) === null || _b === void 0 ? void 0 : _b.id
                    : Array.isArray(data.data)
                        ? (_c = (0, utils_1.last)(data.data)) === null || _c === void 0 ? void 0 : _c.id
                        : undefined);
            const result = yield (client === null || client === void 0 ? void 0 : client.query(query, Object.assign({ fetchMoreArgs: queryArgs || Object.assign({}, (cursor && { [cursorArg]: cursor })) }, options)));
            if ('onSuccess' in options) {
                void (options === null || options === void 0 ? void 0 : options.onSuccess(result));
            }
            return yield new Promise((resolve) => {
                dispatch('SET_DATA', result);
                resolve(result);
            });
        }
        catch (error_) {
            const msg = (0, utils_1.getErrorMessage)(error_);
            dispatch('SET_ERROR', msg);
            throw new Error(msg);
        }
    }), [serialized, loading, error]);
    return argsReady
        ? {
            loading,
            error,
            data,
            fetchMore,
        }
        : {
            data: null,
            loading: withoutArgs === 'WAIT',
            fetchMore,
        };
}
exports.useQuery = useQuery;
